import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Avatar as AvatarMui, Box, Typography } from '@mui/material';
import { type Profile } from 'api';
import { ConditionalLoader } from 'dashboard409a/pages/Operations/components';
import { getInitials } from 'utilities/getInitials';
import { NO_PREPARERS_ASSIGNED, NO_REVIEWERS_ASSIGNED } from '../constants';
import { AvatarContainer } from './Avatar.styles';

interface AvatarProps {
  data?: Profile[];
  name: string;
  roleTitle?: string;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
  style?: CSSProperties;
}

const Avatar = ({ data = [], name, roleTitle, isLoading, sx, style }: AvatarProps) => {
  if (data.length > 1) {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {data.map(item => {
          const displayName = item.first_name ? `${item.first_name} ${item.last_name}` : name;
          return (
            <AvatarContainer key={item.id}>
              <AvatarMui
                alt={displayName}
                src={item.picture_url ?? ''}
                sx={{ width: 32, height: 32, fontSize: '1rem', ...sx }}
                style={style}
              >
                {getInitials({ name: displayName })}
              </AvatarMui>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <ConditionalLoader isLoading={isLoading} width={150} height={16}>
                  <Typography fontWeight="bold" fontSize="0.8rem" lineHeight={1}>{displayName}</Typography>
                </ConditionalLoader>
                <ConditionalLoader isLoading={isLoading} width={100} height={14}>
                  <Typography fontSize="0.75rem" color="text.secondary" lineHeight={1}>{roleTitle}</Typography>
                </ConditionalLoader>
              </Box>
            </AvatarContainer>
          );
        })}
      </Box>
    );
  }

  const shouldShowQuestionMark = name === NO_PREPARERS_ASSIGNED || name === NO_REVIEWERS_ASSIGNED;

  return (
    <AvatarContainer>
      <AvatarMui
        alt={name}
        src={data?.[0]?.picture_url ?? ''}
        sx={{ width: 32, height: 32, fontSize: '1rem', ...sx }}
        style={style}
      >
        {shouldShowQuestionMark ? '?' : getInitials({ name })}
      </AvatarMui>
      <Box display="flex" flexDirection="column" gap={0}>
        <ConditionalLoader isLoading={isLoading} width={150} height={16}>
          <Typography fontWeight="bold" fontSize="0.8rem" lineHeight={1.2}>{name}</Typography>
        </ConditionalLoader>
        <ConditionalLoader isLoading={isLoading} width={100} height={14}>
          <Typography fontSize="0.75rem" color="text.secondary" lineHeight={1.2}>{roleTitle}</Typography>
        </ConditionalLoader>
      </Box>
    </AvatarContainer>
  );
};

export default Avatar;
